import { Box, Txt } from '../ui/primitives';
import { groupStyle } from '../theme/statusTheme';
import type { StatusDefinition } from '../api/types';

/**
 * Status pill.
 *
 * The coloured dot sits beside the status *name* — never the colour alone — so
 * the badge stays readable under any form of colour vision deficiency and in
 * forced-colors mode.
 */
export function StatusBadge({ status, catalog }: { status: string; catalog: StatusDefinition[] }): JSX.Element {
  const def = catalog.find((s) => s.value === status);
  const style = groupStyle(def?.group ?? 'unknown');

  return (
    <Box
      className="inline-flex items-center gap-1.5 pl-1.5 pr-2 py-0.5 rounded-md ring-1 ring-hairline bg-surface-sunken whitespace-nowrap"
      title={def?.description}
    >
      <Box className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: style.fill }} aria-hidden />
      <Txt className="text-[12px] font-medium text-ink-primary">{def?.label ?? status}</Txt>
    </Box>
  );
}

/** "Pending Pickup" / "Picked Up" — the operational read of a status. */
export function PickupBadge({ status, catalog }: { status: string; catalog: StatusDefinition[] }): JSX.Element {
  const def = catalog.find((s) => s.value === status);
  const pending = def?.awaitingPickup ?? false;
  const picked = def?.pickedUp ?? false;
  const label = pending ? 'Pending Pickup' : picked ? 'Picked Up' : 'Not Shipped';
  const color = pending ? 'var(--life-pending)' : picked ? 'var(--life-delivered)' : 'var(--life-cancelled)';

  return (
    <Box className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <Box className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} aria-hidden />
      <Txt className={`text-[12px] ${pending ? 'font-semibold text-ink-primary' : 'text-ink-secondary'}`}>
        {label}
      </Txt>
    </Box>
  );
}
